import { logger } from "@/helpers/tslog";
import { httpClient } from "../api/client";

export async function fetchFormatStyleData(formatId: string) {
  try {
    const { data } = await httpClient.get("/format-style-metadata", {
      params: { formatId },
    });

    return JSON.parse(data.style_metaData);
  } catch (err) {
    logger.error("Error fetching format style data:", err);
  }
}

function lightenColor(hex: string, percentage: number): string {
  // Convert HEX to RGB
  const bigint = parseInt(hex.replace("#", ""), 16);
  let r = (bigint >> 16) & 255;
  let g = (bigint >> 8) & 255;
  let b = bigint & 255;

  // Apply lightness formula
  r = Math.round(r + (255 - r) * percentage);
  g = Math.round(g + (255 - g) * percentage);
  b = Math.round(b + (255 - b) * percentage);

  // Convert back to HEX
  const toHex = (c: number) => c.toString(16).padStart(2, "0");
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

export function parseTheme(theme) {
  const themeColors = theme.themeColors;

  const chartColorKeys = [
    "a:accent1",
    "a:accent2",
    "a:accent3",
    "a:accent4",
    "a:accent5",
    "a:accent6",
    "a:dk1",
    "a:lt1",
    "a:dk2",
    "a:lt2",
  ];

  const chartColors = chartColorKeys.map((key, i) => ({
    name: `Color${i + 1}`,
    fillColor: themeColors[key].color,
    textColor: themeColors[key].textColor,
  }));

  // Table: 5 accent colors
  const tableColorKeys = ["a:accent1", "a:accent2", "a:accent3", "a:accent4", "a:accent5"];

  const tableColors = tableColorKeys.map((key, i) => ({
    name: `Color${i + 1}`,
    fillColor: lightenColor(themeColors[key].color, 0.8),
    textColor: themeColors[key].textColor,
  }));

  const baseStyle = theme?.fonts?.minor || theme?.fonts?.major || "Arial";
  const titleSize = Number(theme?.fontSizes?.title) || 24;
  const subtitleSize = Number(theme?.fontSizes?.subtitle) || Math.max(6, titleSize - 2);
  const axisLikeSize = 8;

  const getRoleColor = (roleKey: string) => {
    const role = theme?.fontColors?.[roleKey];
    if (role) return role;
    // fallback: use dark theme color as default text
    return themeColors?.["a:dk1"]?.color || "#000000";
  };

  const fontsSection = {
    fontStyle: baseStyle,
    fontColor: getRoleColor("body"),
    fontSize: String(titleSize),
    placeholderTitle: {
      style: baseStyle,
      size: String(titleSize),
      color: getRoleColor("title"),
      bold: false,
      italic: false,
      underline: false,
    },
    placeholderSubTitle: {
      style: baseStyle,
      size: String(subtitleSize),
      color: getRoleColor("subtitle"),
      bold: false,
      italic: false,
      underline: false,
    },
    slideTitle: {
      style: baseStyle,
      size: String(titleSize),
      color: getRoleColor("title"),
      bold: false,
      italic: false,
      underline: false,
    },
    slideSubTitle: {
      style: baseStyle,
      size: String(subtitleSize),
      color: getRoleColor("subtitle"),
      bold: false,
      italic: false,
      underline: false,
    },
    keyTakeAways: {
      style: baseStyle,
      size: String(subtitleSize),
      color: getRoleColor("subtitle"),
      bold: false,
      italic: false,
      underline: false,
    },
    placeholderAxisLabels: {
      style: baseStyle,
      size: String(axisLikeSize),
      color: getRoleColor("axisLabel"),
      bold: false,
      italic: false,
      underline: false,
    },
    source: {
      style: baseStyle,
      size: String(axisLikeSize),
      color: getRoleColor("source"),
      bold: false,
      italic: false,
      underline: false,
      approach: "Centralized" as const,
    },
    footnotes: {
      style: baseStyle,
      size: String(axisLikeSize),
      color: getRoleColor("footnotes"),
      bold: false,
      italic: false,
      underline: false,
      approach: "Centralized" as const,
    },
  };

  return { chartColors, tableColors, fontsSection };
}